/**
 * Bundle Builder Section
 * Handles: tab switching, product selection (max 6),
 *          bundle slot display, total price, add-to-cart via AJAX.
 */

class BundleBuilder {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.maxSlots = 6;

    /** @type {Array<{productId:string, variantId:string, price:number, title:string, image:string}>} */
    this.selectedItems = [];

    // DOM refs
    this.countEl    = container.querySelector(`#BundleCount-${this.sectionId}`);
    this.totalEl    = container.querySelector(`#BundleTotal-${this.sectionId}`);
    this.slotsEl    = container.querySelector(`#BundleSlots-${this.sectionId}`);
    this.ctaEl      = container.querySelector(`#BundleAddCart-${this.sectionId}`);
    this.slots      = Array.from(container.querySelectorAll('.bundle-builder__slot'));
    this.tierChips  = Array.from(container.querySelectorAll('.bundle-builder__tier-chip'));

    this.init();
  }

  // ─────────────────────────────────────────────────────────────
  // Init
  // ─────────────────────────────────────────────────────────────

  init() {
    this.bindTabs();
    this.bindCards();
    this.bindSlotRemove();
    this.bindSwatchUpdates();
    this.bindCTA();
    this.updateUI();
  }

  // ─────────────────────────────────────────────────────────────
  // Tabs
  // ─────────────────────────────────────────────────────────────

  bindTabs() {
    const tabs   = this.container.querySelectorAll('[data-bundle-tab]');
    const panels = this.container.querySelectorAll('[data-bundle-panel]');

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const idx = tab.dataset.bundleTab;

        tabs.forEach((t) => {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        panels.forEach((p) => p.classList.remove('is-active'));

        tab.classList.add('is-active');
        tab.setAttribute('aria-selected', 'true');

        const panel = this.container.querySelector(`[data-bundle-panel="${idx}"]`);
        if (panel) panel.classList.add('is-active');
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Card selection
  // ─────────────────────────────────────────────────────────────

  bindCards() {
    // Intercept ALL link clicks inside card wrappers (capture phase)
    this.container.addEventListener(
      'click',
      (e) => {
        if (e.target.closest('[data-bundle-card]') && e.target.tagName === 'A') {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    // Card selection toggle
    this.container.addEventListener('click', (e) => {
      // Ignore clicks on slot remove button
      if (e.target.closest('.bundle-builder__slot-remove')) return;

      const card = e.target.closest('[data-bundle-card]');
      if (!card) return;

      e.preventDefault();
      const productId = card.dataset.productId;

      if (this.isSelected(productId)) {
        this.removeItem(productId);
      } else if (this.selectedItems.length < this.maxSlots) {
        this.addItem({
          productId,
          variantId: card.dataset.variantId,
          price:     parseInt(card.dataset.productPrice, 10) || 0,
          title:     card.dataset.productTitle,
          image:     card.dataset.productImage,
        });
      }

      this.updateUI();
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Swatch / variant live updates
  // ─────────────────────────────────────────────────────────────

  bindSwatchUpdates() {
    this.container.addEventListener('bundle:swatch-changed', (e) => {
      const { productId, variantId, image } = e.detail;
      const item = this.selectedItems.find((i) => i.productId === productId);
      if (!item) return;
      if (variantId) item.variantId = variantId;
      if (image)     item.image     = image;
      this.updateSlots();
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Slot remove
  // ─────────────────────────────────────────────────────────────

  bindSlotRemove() {
    this.container.addEventListener('click', (e) => {
      const btn = e.target.closest('.bundle-builder__slot-remove');
      if (!btn) return;

      e.stopPropagation();
      const slot      = btn.closest('.bundle-builder__slot');
      const slotIndex = parseInt(slot.dataset.slotIndex, 10);

      if (this.selectedItems[slotIndex]) {
        this.removeItem(this.selectedItems[slotIndex].productId);
        this.updateUI();
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // CTA
  // ─────────────────────────────────────────────────────────────

  bindCTA() {
    this.ctaEl.addEventListener('click', () => this.addToCart());
  }

  // ─────────────────────────────────────────────────────────────
  // Data helpers
  // ─────────────────────────────────────────────────────────────

  isSelected(productId) {
    return this.selectedItems.some((item) => item.productId === productId);
  }

  addItem(data) {
    if (this.selectedItems.length >= this.maxSlots) return;
    this.selectedItems.push(data);
  }

  removeItem(productId) {
    this.selectedItems = this.selectedItems.filter((item) => item.productId !== productId);
  }

  // ─────────────────────────────────────────────────────────────
  // UI updates
  // ─────────────────────────────────────────────────────────────

  updateUI() {
    this.updateCards();
    this.updateSlots();
    this.updateCount();
    this.updateTotal();
    this.updateCTA();
    this.updateTiers();
  }

  updateTiers() {
    if (!this.tierChips.length) return;
    const count = this.selectedItems.length;
    // Find the highest tier whose threshold has been reached
    let activeTier = null;
    this.tierChips.forEach((chip) => {
      const threshold = parseInt(chip.dataset.tierThreshold, 10) || 0;
      if (count >= threshold) activeTier = chip;
    });
    this.tierChips.forEach((chip) => {
      chip.classList.toggle('is-active', chip === activeTier);
    });
  }

  updateCards() {
    const isFull = this.selectedItems.length >= this.maxSlots;
    const cards  = this.container.querySelectorAll('[data-bundle-card]');

    cards.forEach((card) => {
      const selected = this.isSelected(card.dataset.productId);
      card.classList.toggle('is-selected', selected);
      // Dim unselectable cards when slots are full
      card.classList.toggle('is-maxed', isFull && !selected);
    });
  }

  updateSlots() {
    this.slots.forEach((slot, i) => {
      const item = this.selectedItems[i];
      const img  = slot.querySelector('.bundle-builder__slot-img');

      if (item) {
        slot.classList.add('is-filled');
        img.src = item.image || '';
        img.alt = item.title || '';
      } else {
        slot.classList.remove('is-filled');
        img.src = '';
        img.alt = '';
      }
    });
  }

  updateCount() {
    this.countEl.textContent = this.selectedItems.length;
  }

  updateTotal() {
    const cents = this.selectedItems.reduce((sum, item) => sum + (item.price || 0), 0);
    this.totalEl.textContent = this.formatMoney(cents);
  }

  updateCTA() {
    const hasItems = this.selectedItems.length > 0;
    this.ctaEl.disabled = !hasItems;
    this.ctaEl.setAttribute('aria-disabled', String(!hasItems));
  }

  formatMoney(cents) {
    // Use Shopify currency format if available
    if (window.Shopify && window.Shopify.currency) {
      return (cents / 100).toLocaleString(undefined, {
        style:    'currency',
        currency: window.Shopify.currency.active || 'USD',
      });
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // ─────────────────────────────────────────────────────────────
  // Add to cart (AJAX)
  // ─────────────────────────────────────────────────────────────

  async addToCart() {
    if (this.selectedItems.length === 0) return;

    const originalLabel = this.ctaEl.textContent;
    this.ctaEl.textContent = 'Adding…';
    this.ctaEl.classList.add('is-loading');
    this.ctaEl.disabled = true;

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cartEl = cartDrawer || cartNotification;

      const root = window.Shopify?.routes?.root || '/';

      // Single request with all items at once using FormData
      const formData = new FormData();
      this.selectedItems.forEach((item) => {
        formData.append('items[][id]', parseInt(item.variantId, 10));
        formData.append('items[][quantity]', 1);
      });

      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        formData.append('sections', cartEl.getSectionsToRender().map((s) => s.id));
        formData.append('sections_url', window.location.pathname);
        if (typeof cartEl.setActiveElement === 'function') {
          cartEl.setActiveElement(document.activeElement);
        }
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.description || 'Cart error');
      }

      const parsedResponse = await response.json();

      if (cartEl && typeof cartEl.renderContents === 'function') {
        cartEl.renderContents(parsedResponse);
        cartEl.classList.remove('is-empty');
        document.body.classList.add('overflow-hidden');
      }

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder', cartData: parsedResponse });
      }

      this.selectedItems = [];
      this.updateUI();

    } catch (err) {
      console.error('[BundleBuilder] Add to cart failed:', err);
      alert(err.message || 'Something went wrong. Please try again.');
    } finally {
      this.ctaEl.textContent = originalLabel;
      this.ctaEl.classList.remove('is-loading');
      this.updateCTA();
    }
  }
}

// ── Bootstrap ────────────────────────────────────────────────
document.querySelectorAll('[id^="BundleBuilder-"]').forEach((el) => {
  new BundleBuilder(el);
});
